import { useEffect, useState } from "react";
import api from "../lib/typedApi";
import { PageHeader, NBCard, NBInput, NBSelect, NBButton, NBTextarea, EmptyState } from "../components/UI";
import { toast } from "sonner";
import { Plus, Trash2, X, ChefHat, Clock, Users, Link2 } from "lucide-react";

export default function Recipes() {
  const [recipes, setRecipes] = useState([]);
  const [plans, setPlans] = useState([]);
  const [tab, setTab] = useState("recipes");
  const [open, setOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", ingredients: "", instructions: "", prep_time: 30, servings: 4, image_url: "", category: "dinner" });
  const [planForm, setPlanForm] = useState({ date: "", meal_type: "dinner", recipe_id: "", custom_meal: "", notes: "" });

  const load = async () => {
    const [r, p] = await Promise.all([api.get("/api/recipes"), api.get("/api/meal-plans")]);
    setRecipes(r.data);
    setPlans(p.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/recipes", {
        ...form,
        ingredients: form.ingredients.split("\n").filter(Boolean),
        instructions: form.instructions.split("\n").filter(Boolean),
      });
      toast.success("Recipe saved!");
      setOpen(false);
      setForm({ title: "", description: "", ingredients: "", instructions: "", prep_time: 30, servings: 4, image_url: "", category: "dinner" });
      load();
    } catch { toast.error("Failed"); }
  };

  const submitPlan = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/meal-plans", planForm);
      toast.success("Meal planned!");
      setPlanOpen(false);
      setPlanForm({ date: "", meal_type: "dinner", recipe_id: "", custom_meal: "", notes: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => { await api.delete(`/api/recipes/${id}`); load(); };
  const removePlan = async (id) => { await api.delete(`/api/meal-plans/${id}`); load(); };

  const importRecipe = async (e) => {
    e.preventDefault();
    if (!importUrl.trim()) return;
    setImporting(true);
    try {
      const { data } = await api.post("/api/recipes/import", { url: importUrl });
      toast.success(`Imported "${data.title}"!`);
      setImportOpen(false);
      setImportUrl("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed — try a different URL");
    }
    setImporting(false);
  };

  return (
    <div data-testid="recipes-page">
      <PageHeader
        title="Recipes & Meals"
        subtitle="Family cookbook and weekly meal planner."
        color="#E8B96B"
        action={
          <div className="flex gap-2 flex-wrap">
            <NBButton testId="import-recipe-btn" color="#A8BFD1" onClick={() => setImportOpen(true)}>
              <Link2 strokeWidth={3} className="w-4 h-4 inline mr-1" />Import URL
            </NBButton>
            <NBButton testId="add-recipe-btn" onClick={() => setOpen(true)}><Plus strokeWidth={3} className="w-4 h-4 inline mr-1" />Recipe</NBButton>
            <NBButton testId="plan-meal-btn" color="#B8CFA9" onClick={() => setPlanOpen(true)}>Plan Meal</NBButton>
          </div>
        }
      />

      <div className="flex gap-2 mb-6">
        {["recipes", "plans"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
            className={`nb-btn px-4 py-2 text-sm capitalize ${tab === t ? "bg-[#E8B96B]" : "bg-white"}`}>{t}</button>
        ))}
      </div>

      {tab === "recipes" && (
        recipes.length === 0 ? (
          <EmptyState title="No recipes yet" subtitle="Add a family favorite." />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recipes.map((r) => (
              <NBCard key={r.id} data-testid={`recipe-${r.id}`}>
                {r.image_url ? (
                  <div className="aspect-video -m-6 mb-4 border-b-2 border-black overflow-hidden bg-[#E8B96B]">
                    <img src={r.image_url} alt={r.title} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="aspect-video -m-6 mb-4 border-b-2 border-black bg-[#E8B96B] flex items-center justify-center">
                    <ChefHat strokeWidth={2.5} className="w-16 h-16" />
                  </div>
                )}
                <div className="flex justify-between items-start">
                  <div>
                    <span className="nb-badge bg-[#E9B5A0]">{r.category}</span>
                    <h4 className="text-xl font-black mt-2" style={{ fontFamily: "var(--hh-display)" }}>{r.title}</h4>
                  </div>
                  <button onClick={() => remove(r.id)} className="nb-btn bg-[#E9B5A0] p-1.5" data-testid={`delete-recipe-${r.id}`}>
                    <Trash2 strokeWidth={2.5} className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex gap-3 mt-2 text-xs font-bold">
                  <span className="flex items-center gap-1"><Clock strokeWidth={2.5} className="w-3 h-3" /> {r.prep_time}m</span>
                  <span className="flex items-center gap-1"><Users strokeWidth={2.5} className="w-3 h-3" /> {r.servings} servings</span>
                </div>
                {r.description && <p className="text-sm mt-2">{r.description}</p>}
                {r.ingredients?.length > 0 && (
                  <details className="mt-3">
                    <summary className="text-sm font-black cursor-pointer">Ingredients ({r.ingredients.length})</summary>
                    <ul className="text-sm mt-2 space-y-1 list-disc list-inside">
                      {r.ingredients.map((i, idx) => <li key={idx}>{i}</li>)}
                    </ul>
                  </details>
                )}
                {r.instructions?.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-sm font-black cursor-pointer">Instructions</summary>
                    <ol className="text-sm mt-2 space-y-1 list-decimal list-inside">
                      {r.instructions.map((i, idx) => <li key={idx}>{i}</li>)}
                    </ol>
                  </details>
                )}
              </NBCard>
            ))}
          </div>
        )
      )}

      {tab === "plans" && (
        plans.length === 0 ? (
          <EmptyState title="No meals planned" subtitle="Start planning the week's meals." />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {plans.map((p) => {
              const recipe = recipes.find((r) => r.id === p.recipe_id);
              return (
                <NBCard key={p.id} color="#B8CFA9" data-testid={`plan-${p.id}`}>
                  <div className="flex justify-between">
                    <span className="nb-badge bg-white capitalize">{p.meal_type}</span>
                    <button onClick={() => removePlan(p.id)} className="nb-btn bg-white p-1.5">
                      <Trash2 strokeWidth={2.5} className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs font-bold mt-2">{p.date}</p>
                  <h4 className="text-lg font-black mt-1" style={{ fontFamily: "var(--hh-display)" }}>{recipe?.title || p.custom_meal}</h4>
                  {p.notes && <p className="text-sm mt-2">{p.notes}</p>}
                </NBCard>
              );
            })}
          </div>
        )
      )}

      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="nb-card max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "8px 8px 0 0 rgba(0,0,0,1)" }}>
            <div className="flex justify-between mb-4">
              <h2 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>New Recipe</h2>
              <button onClick={() => setOpen(false)} className="nb-btn bg-[#E9B5A0] p-1.5"><X strokeWidth={3} className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <NBInput testId="recipe-title" required placeholder="Recipe name" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <NBSelect value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                <option>breakfast</option><option>lunch</option><option>dinner</option><option>snack</option><option>dessert</option>
              </NBSelect>
              <NBTextarea testId="recipe-desc" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <NBInput placeholder="Image URL (optional)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <NBInput type="number" placeholder="Prep min" value={form.prep_time} onChange={(e) => setForm({ ...form, prep_time: parseInt(e.target.value) || 0 })} />
                <NBInput type="number" placeholder="Servings" value={form.servings} onChange={(e) => setForm({ ...form, servings: parseInt(e.target.value) || 1 })} />
              </div>
              <NBTextarea testId="recipe-ingredients" placeholder="Ingredients (one per line)" value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} />
              <NBTextarea testId="recipe-instructions" placeholder="Steps (one per line)" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} />
              <NBButton testId="submit-recipe" type="submit" className="w-full">Save Recipe</NBButton>
            </form>
          </div>
        </div>
      )}

      {importOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setImportOpen(false)}>
          <div className="nb-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "8px 8px 0 0 rgba(0,0,0,1)" }}>
            <div className="flex justify-between mb-4">
              <h2 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>Import from URL</h2>
              <button onClick={() => setImportOpen(false)} className="nb-btn bg-[#E9B5A0] p-1.5"><X strokeWidth={3} className="w-4 h-4" /></button>
            </div>
            <p className="text-sm mb-3 text-neutral-700">Paste a recipe URL — we'll fetch the page and let GPT extract title, ingredients, and steps for you.</p>
            <form onSubmit={importRecipe} className="space-y-3">
              <NBInput
                testId="import-url-input"
                required
                type="url"
                placeholder="https://www.bbcgoodfood.com/recipes/..."
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                autoFocus
              />
              <NBButton testId="submit-import" type="submit" disabled={importing} color="#A8BFD1" className="w-full">
                <Link2 strokeWidth={3} className="w-4 h-4 inline mr-1" />
                {importing ? "Importing..." : "Import Recipe"}
              </NBButton>
            </form>
            <p className="text-xs text-neutral-500 mt-3">Tip: works best with major recipe sites. Some sites block scraping; try another if it fails.</p>
          </div>
        </div>
      )}

      {planOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setPlanOpen(false)}>
          <div className="nb-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "8px 8px 0 0 rgba(0,0,0,1)" }}>
            <div className="flex justify-between mb-4">
              <h2 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>Plan a Meal</h2>
              <button onClick={() => setPlanOpen(false)} className="nb-btn bg-[#E9B5A0] p-1.5"><X strokeWidth={3} className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submitPlan} className="space-y-3">
              <NBInput testId="plan-date" required type="date" value={planForm.date} onChange={(e) => setPlanForm({ ...planForm, date: e.target.value })} />
              <NBSelect testId="plan-meal-type" value={planForm.meal_type} onChange={(e) => setPlanForm({ ...planForm, meal_type: e.target.value })}>
                <option>breakfast</option><option>lunch</option><option>dinner</option><option>snack</option>
              </NBSelect>
              <NBSelect value={planForm.recipe_id} onChange={(e) => setPlanForm({ ...planForm, recipe_id: e.target.value })}>
                <option value="">Pick recipe (optional)</option>
                {recipes.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
              </NBSelect>
              <NBInput placeholder="Or custom meal" value={planForm.custom_meal} onChange={(e) => setPlanForm({ ...planForm, custom_meal: e.target.value })} />
              <NBInput placeholder="Notes" value={planForm.notes} onChange={(e) => setPlanForm({ ...planForm, notes: e.target.value })} />
              <NBButton testId="submit-plan" type="submit" color="#B8CFA9" className="w-full">Save Plan</NBButton>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
